import styled from 'styled-components'
import { themeGet } from 'styled-system'
import { Button } from 'grommet'
import { BodyCopy, HeaderOne } from 'secondstep-components'
import {
  getHighlightColor,
  getBackgroundGradient,
} from 'utils/highschoolHelpers'
import { variables } from 'themes'
import { DefaultSiteSelectorWrapper } from 'components/SiteSelector'
import { MarkdownParagraph } from 'components/MarkdownComponent'
import TextLinkWithFileIcon from 'components/TextLinkWithFileIcon'

export const tabTheme = {
  colors: {
    buttons: {
      tabActive: variables.colors.hsPrimaryPink,
      tabDefault: variables.colors.darkGray,
    },
  },
  font: {
    molde: {
      medium: variables.font.molde.medium,
      regular: variables.font.molde.regular,
      semiBold: variables.font.molde.semiBold,
    },
  },
}

export const ActivityButton = styled(Button)`
  background: ${themeGet('colors.buttons.primary.bgActive')};
  color: ${themeGet('colors.buttons.primary.titleActive')};
  border-radius: 0.5rem;
  padding: 1.3125rem;
  padding-top: 0.688rem;
  padding-bottom: 0.563rem;
  margin-bottom: 1.3125rem;
  font-family: ${themeGet('font.molde.medium')};
  font-size: 1rem;
  text-align: center;
  width: ${props => (props.$isHighSchoolButton ? '9.375rem' : 'auto')};
  height: ${props => (props.$isHighSchoolButton ? '2.6875rem' : 'auto')};
  min-width: ${props => (props.$isHighSchoolButton ? '9.375rem' : 'auto')};
  min-height: ${props => (props.$isHighSchoolButton ? '2.6875rem' : 'auto')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  ${props =>
    props.$isHighSchoolButton
      ? `
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    line-height: 1;
  `
      : ''}

  ${themeGet('breakpoints.mobileLarge')} {
    border-radius: 0.3rem;
    margin-bottom: 2.55rem;
  }
  :hover {
    background: ${themeGet('colors.buttons.primary.bgHover')};
    color: ${themeGet('colors.buttons.primary.titleHover')};
  }
`

export const AudienceTagContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
  ${themeGet('breakpoints.mobileLarge')} {
    justify-content: flex-start;
    margin-top: 1.25rem;
    margin-bottom: 1.75rem;
  }
  ${themeGet('breakpoints.tabletLarge')} {
    justify-content: flex-start;
    margin-top: 1.25rem;
    margin-bottom: 1.75rem;
  }
  div {
    background-color: ${props =>
      props.isHighSchool
        ? getHighlightColor(props.audience)
        : themeGet('individualResource.audienceTag.backgroundColor')};

    color: ${themeGet('individualResource.audienceTag.color')};
    border-radius: 0.3rem;
    padding: 0rem 0.75rem;
    font-size: 0.75em;
  }
`

export const ControlPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 1.25rem 3.5625rem 1.25rem;
  .content {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: ${themeGet('breakpoints.sizes.large')};
    ${themeGet('breakpoints.mobileLarge')} {
      flex-direction: column-reverse;
      padding-bottom: 0.125rem;
    }
  }

  // targets the TabSet buttons:
  [class*='StyledGrommet'] {
    align-self: last baseline;
    margin-bottom: 1.3rem;
    ${themeGet('breakpoints.mobileLarge')} {
      align-self: first baseline;
    }
  }
`

export const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-bottom: 2.5rem;
  background: ${props =>
    props.isHighSchool ? getBackgroundGradient(props.audience) : '#eef0f2'};
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: ${themeGet('breakpoints.sizes.large')};
  }
`

export const ExternalLinkIcon = styled.img`
  margin-left: 0.5rem;
  margin-bottom: -0.15rem;
`
export const ResourceImageContainer = styled.img`
  max-width: 12.5rem;
  max-height: 8.75rem;
  width: 100%;
  height: auto;
  object-fit: contain;

  ${themeGet('breakpoints.tabletLarge')} {
    order: -1;
  }
`

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.3rem;
  ${themeGet('breakpoints.large')} {
    margin: 1.3rem 1.25rem;
  }
  ${themeGet('breakpoints.mobileLarge')} {
    margin: 1.3rem 1rem;
  }
  .content {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: ${themeGet('breakpoints.sizes.large')};
    ${themeGet('breakpoints.mobileLarge')} {
      flex-direction: column-reverse;
      align-items: flex-start;
      text-align: left;
    }
    ${themeGet('breakpoints.tabletLarge')} {
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
    }
  }

  .resource-image {
    // fixed dimensions on desktop
    width: 21.438rem;
    height: 11.625rem;
    ${themeGet('breakpoints.mobileLarge')} {
      width: unset;
      height: unset;
      max-width: 100%;
    }
  }
`

export const PageContainer = styled.div`
  width: 100%;
  // background-color: #eef0f2;
`

export const StyledHeaderOne = styled(HeaderOne)`
  color: ${themeGet('colors.heading.level.1')};
  font-family: ${themeGet('individualResource.h1.fontFamily')};
  ${themeGet('breakpoints.mobileLarge')} {
    text-align: left;
  }
  ${themeGet('breakpoints.tabletLarge')} {
    text-align: left;
  }
`

export const SurveyLink = styled.a`
  display: block;
  color: ${themeGet('colors.highschool.green2')};
  font-family: ${themeGet('font.molde.medium')};
  font-size: 1.125rem;
  text-decoration: none;
  margin-top: 1.563rem;
  :visited {
    color: ${themeGet('colors.highschool.green2')};
  }
  :focus,
  :hover {
    color: ${themeGet('colors.highschool.green1')};
  }
`

export const BannerSpacer = styled.div`
  margin-top: 6.99rem;
  div {
    margin-top: -6.99rem !important;
  }
`
export const SiteSelectorWrapper = styled(DefaultSiteSelectorWrapper)`
  margin: auto;
  max-width: ${themeGet('breakpoints.sizes.large')};
  padding-right: 0;
  padding-top: 0;
  margin-top: ${props => (props.isHighSchool ? '1.593rem' : 'inherit')};
  ${themeGet('breakpoints.large')} {
    padding-right: 1rem;
  }
`

export const HsIndividualResourceMarkCompleteWrapper = styled.div`
  margin-top: 1.5rem;
`

export const HeaderTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  ${themeGet('breakpoints.mobileLarge')} {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
  ${themeGet('breakpoints.tabletLarge')} {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
`

export const TitleWrapper = styled.div`
  flex: 1;
  max-width: calc(100% - 12.5rem);

  ${themeGet('breakpoints.mobileLarge')} {
    max-width: 100%;
  }
  ${themeGet('breakpoints.tabletLarge')} {
    max-width: 100%;
  }
`

export const HeaderContentWrapper = styled.div`
  width: ${props => (props.isHighSchool ? 'auto' : '100%')};
  ${themeGet('breakpoints.tabletLarge')} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    width: 100%;

    p {
      max-width: 100%;
    }
  }
`

export const MetadataContainer = styled.div`
  margin-bottom: ${props => (!props.isHighSchool ? '4.8125rem' : '0')};
`
export const ResourceContainer = styled.div`
  background-color: ${props =>
    !props.isHighSchool ? '#eef0f2' : 'transparent'};
`

export const BreadcrumbWrapper = styled.nav`
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: ${themeGet('breakpoints.sizes.large')};
  margin: auto;
  padding: 1.25rem 1.25rem 1.625rem 0;

  ${themeGet('breakpoints.mobileLarge')} {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  ${themeGet('breakpoints.large')} {
    padding-left: 1.25rem;
  }
`
export const NavigationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  max-width: ${themeGet('breakpoints.sizes.large')};
  margin: auto;
  ${themeGet('breakpoints.mobileLarge')} {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const StyledMarkdownParagraph = styled(MarkdownParagraph)`
  margin-bottom: 1rem;
  line-height: 1.5;
  ${themeGet('breakpoints.tabletLarge')} {
    display: block;
    margin-bottom: 0;
  }
`

export const PreviewBannerWrapper = styled.div`
  > div:nth-child(2) {
    margin-top: -6.99rem !important;
  }
`

export const WeeklyPlanOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background-color: white;
  border: 0.09375rem solid
    ${props =>
      props.isHighSchool
        ? getHighlightColor(props.audience)
        : themeGet('individualResource.metaData.h4.backgroundColor')};
  border-radius: 0.5rem;
  margin: ${props =>
    props.isHighSchool ? '0 0 1.3rem' : '-3.6875rem 0 1.3rem'};
  @media screen and (max-width: 87.5rem) {
    max-width: calc(100% - 2.5rem);
  }
  ${themeGet('breakpoints.tablet')} {
    max-width: 100%;
  }
`

export const WeeklyPlanHeaderContainer = styled.div`
  align-self: baseline;
  box-sizing: border-box;
  font-family: ${themeGet('individualResource.h4.fontFamily')};
  font-size: 1.25rem;
  color: ${themeGet('individualResource.h4.color')};
  padding-top: 1.3rem;
  padding-bottom: 1.6rem;
  background-color: ${themeGet(
    'individualResource.metaData.h4.backgroundColor',
  )};
  width: 100%;
  padding-left: 1.5625rem;
  border-radius: 0.4rem 0.4rem 0 0;
`

export const WeeklyPlanContentContainer = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  background-color: white;
  padding: 1.5rem 1.75rem 1.3rem 1.25rem;
  line-height: 1.5rem;

  ${themeGet('breakpoints.tablet')} {
    padding: 1.25rem;
    padding-bottom: 2.5rem;
  }

  li::marker {
    color: ${themeGet('colors.markerOverride')};
  }
  li:not(:last-child) {
    margin-bottom: 0.5rem;
  }
  h2,
  h3,
  h4 {
    font-family: ${themeGet('font.komet')};
    margin: 0;
  }
  h2 {
    color: ${themeGet('colors.heading.level.2')};
    font-weight: ${themeGet('individualResource.activityDetails.h2.weight')};
    font-size: ${themeGet('individualResource.activityDetails.h2.size')};
    font-family: ${themeGet('individualResource.activityDetails.h2.family')};
    padding-top: 1.25rem;
    padding-bottom: 0;
  }
  h3,
  h4 {
    font-weight: bold;
  }
  h3 {
    color: ${themeGet('colors.heading.level.3')};
    font-size: 1.25rem;
    padding-top: 0.875rem;
    padding-bottom: 0.75rem;
  }
  h4 {
    color: ${themeGet('colors.heading.level.4')};
    font-size: 1.0625rem;
    padding-top: 0.375rem;
    padding-bottom: 0.35rem;
  }
  a,
  a:visited {
    text-decoration: none;
    color: ${themeGet('individualResource.activityDetails.a.color')};
  }
  a:focus,
  a:hover {
    color: ${themeGet('individualResource.activityDetails.a.hover.color')};
  }
  p,
  li,
  span {
    line-height: 1.5rem;
  }
  p:not(:last-child) {
    margin-block-end: 0.87rem;
  }
  ${BodyCopy} {
    padding-bottom: 0;
  }
  [class*='r-marginTop'] {
    margin-top: 0;
  }
  sup {
    color: ${themeGet('colors.hsPrimaryPink')};
  }
  ul,
  ol {
    margin-block-end: 0.87rem;
    padding-left: 1.5rem;
  }
  [class*='ImageCalloutButton'] {
    padding-bottom: 1.75rem;
  }
`

export const PhaseTextLinkWithFileIcon = styled(TextLinkWithFileIcon)`
  margin-bottom: 0.5rem;
  color: ${themeGet('colors.hsPrimaryPink')};
  fill: ${themeGet('colors.hsPrimaryPink')};

  :hover {
    color: ${themeGet('colors.highschool.green1')};
    fill: ${themeGet('colors.highschool.green1')};
  }
`

export const SectionContainer = styled.section`
  width: 100%;
`

export const SectionTitle = styled.h2`
  color: ${themeGet('colors.heading.level.2')};
  font-family: ${themeGet('individualResource.activityDetails.h2.family')};
  font-size: ${themeGet('individualResource.activityDetails.h2.size')};
  margin: 0;
`

export const Divider = styled.hr`
  border: 0;
  border-top: 0.0625rem solid ${themeGet('colors.grayScale.30', '#d8d8d8')};
  margin: 1.5rem 0;
  width: 100%;
`

export const SectionButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;
  max-width: 100%;
  margin-left: auto;

  @media screen and (max-width: 25rem) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.75rem;
    width: 100%;
  }

  button {
    margin-bottom: 0 !important;
  }
`

export const SectionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  min-width: 0;

  ${themeGet('breakpoints.tablet')} {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const PhaseContentWrapper = styled.div`
  width: 100%;
`

export const PhaseSectionBody = styled.div`
  width: 100%;
  max-width: ${props => (props.isHighSchool ? '55rem' : '100%')};
`

export const MarkCompleteContainer = styled.div`
  [data-print-hidden='mark-done'] {
    margin: 0;
  }

  button {
    margin-bottom: 0 !important;
  }
`

export const AdvisoryActivitiesContainer = styled.section`
  width: 100%;
  margin-top: 1.5rem;
`

export const AdvisoryActivitiesTitle = styled.h3`
  margin: 0 0 1rem;
  color: ${themeGet('colors.heading.level.3')};
  font-size: 1.25rem;
`

export const AdvisoryInlineLinks = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
`

export const AdvisoryInlineLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  min-height: 1rem;

  .advisory-inline-icon {
    color: ${props => props.iconColor};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  .advisory-inline-icon svg,
  .advisory-inline-icon .icon {
    width: 1rem;
    height: 1rem;
  }

  .advisory-inline-icon svg [fill]:not([fill='none']),
  .advisory-inline-icon .icon [fill]:not([fill='none']) {
    fill: currentColor !important;
  }

  .advisory-inline-icon svg [stroke]:not([stroke='none']),
  .advisory-inline-icon .icon [stroke]:not([stroke='none']) {
    stroke: currentColor !important;
  }

  .advisory-inline-icon svg,
  .advisory-inline-icon .icon,
  .advisory-inline-icon svg *,
  .advisory-inline-icon .icon * {
    opacity: 1 !important;
    fill-opacity: 1 !important;
    stroke-opacity: 1 !important;
  }

  svg {
    flex-shrink: 0;
  }
`

export const AdvisoryInlineLink = styled.a`
  display: block;
  text-decoration: none;
  color: ${themeGet('individualResource.activityDetails.a.color')};

  &:visited {
    color: ${themeGet('individualResource.activityDetails.a.color')};
  }

  &:focus,
  &:hover {
    color: ${themeGet('individualResource.activityDetails.a.hover.color')};
  }
`

export const AdvisoryActivitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  ${themeGet('breakpoints.mobileLarge')} {
    grid-template-columns: 1fr;
  }
`
